import { useState, useMemo } from "react";
import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";
import Table from "components/Table";
import InputField from "components/InputField";
import { LeaveDetailsType } from "pages/leaves/Leaves";
import { Link } from "react-router-dom";
import { MdVisibility } from 'react-icons/md';
import SelectField from "components/SelectField";

interface UnifiedLeavesTablePropsType {
  pendingData: LeaveDetailsType[];
  approvedData: LeaveDetailsType[];
  declinedData: LeaveDetailsType[];
  isManager?: boolean;
}

type LeaveStatusFilter = "pending" | "approved" | "declined";

const UnifiedLeavesTable = (props: UnifiedLeavesTablePropsType) => {
  const { pendingData, approvedData, declinedData, isManager } = props;
  const [statusFilter, setStatusFilter] = useState<LeaveStatusFilter>("pending");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const columnHelper = createColumnHelper<LeaveDetailsType>();

  // Get current data based on selected filter
  const getCurrentData = () => {
    switch (statusFilter) {
      case "pending":
        return pendingData;
      case "approved":
        return approvedData;
      case "declined":
        return declinedData;
      default:
        return pendingData;
    }
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    const currentData = getCurrentData();
    
    if (!searchTerm.trim()) {
      return currentData;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return currentData.filter((leave) => {
      const employeeName = `${leave.employee_first_name} ${leave.employee_last_name}`.toLowerCase();
      const leaveType = leave.leave_type_description?.toLowerCase() || "";
      
      return (
        employeeName.includes(searchLower) ||
        leaveType.includes(searchLower)
      );
    });
  }, [statusFilter, searchTerm, pendingData, approvedData, declinedData]);

  // Get table heading based on filter
  const getTableHeading = () => {
    switch (statusFilter) {
      case "pending":
        return isManager ? "Team Leave Requests" : "Pending Leaves";
      case "approved":
        return isManager ? "Manager Approved Leaves" : "Approved Leaves";
      case "declined":
        return isManager ? "Manager Declined Leaves" : "Declined Leaves";
      default:
        return "Leaves";
    }
  };

  // Helper function to get status badge
  const getStatusBadge = (status: string | null | undefined) => {
    if (!status) {
      return (
        <span className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 px-2 py-0.5 rounded-full text-[10px] font-medium">
          Unknown
        </span>
      );
    }
    
    // Normalize status to uppercase string
    const normalizedStatus = String(status).toUpperCase().trim();
    
    const statusConfig = {
      A: {
        label: "Approved",
        className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
        icon: "fas fa-check-circle",
      },
      P: {
        label: "Pending",
        className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
        icon: "fas fa-clock",
      },
      D: {
        label: "Declined",
        className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
        icon: "fas fa-times-circle",
      },
      MA: {
        label: "Manager Approved",
        className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
        icon: "fas fa-user-check",
      },
      MD: {
        label: "Manager Declined",
        className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
        icon: "fas fa-user-times",
      },
      CA: {
        label: "Client Approved",
        className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
        icon: "fas fa-check-circle",
      },
      CR: {
        label: "Client Rejected",
        className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
        icon: "fas fa-times-circle",
      },
    };

    const config = statusConfig[normalizedStatus as keyof typeof statusConfig];
    if (!config) {
      return (
        <span className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 px-2 py-0.5 rounded-full text-[10px] font-medium">
          Unknown
        </span>
      );
    }

    return (
      <span className={`${config.className} px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1 w-fit`}>
        <i className={`${config.icon} text-[9px]`}></i>
        {config.label}
      </span>
    );
  };

  // Helper function to get leave type icon
  const getLeaveTypeIcon = (leaveType: string) => {
    const type = leaveType?.toLowerCase();
    if (type?.includes("sick")) return "fas fa-user-injured";
    if (type?.includes("annual") || type?.includes("vacation")) return "fas fa-umbrella-beach";
    if (type?.includes("maternity") || type?.includes("paternity")) return "fas fa-baby";
    if (type?.includes("emergency")) return "fas fa-exclamation-triangle";
    return "fas fa-calendar-day";
  };

  // Helper function to calculate days between dates
  const calculateDays = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end day
    return diffDays;
  };

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const columns: ColumnDef<LeaveDetailsType, any>[] = [
    columnHelper.accessor("employee_id", {
      id: "employee_id",
      header: () => <TableHeader>EMPLOYEE ID</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="font-mono text-xs text-gray-600 dark:text-gray-400">#{info.getValue()}</span>
        </TableCell>
      ),
    }),
    columnHelper.accessor((row) => `${row.employee_first_name} ${row.employee_last_name}`, {
      id: "name",
      header: () => <TableHeader>NAME</TableHeader>,
      cell: (info) => (
        <TableCell>
          <div className="flex items-center gap-1.5">
            <div className="w-7 h-7 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="fas fa-user text-purple-600 dark:text-purple-400 text-[10px]"></i>
            </div>
            <span className="font-medium text-gray-900 dark:text-white text-xs">{info.getValue()}</span>
          </div>
        </TableCell>
      ),
    }),
    columnHelper.accessor("leave_type_description", {
      id: "leave_type_description",
      header: () => <TableHeader>TYPE</TableHeader>,
      cell: (info) => (
        <TableCell>
          <div className="flex items-center gap-1.5">
            <div className="w-7 h-7 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <i className={`${getLeaveTypeIcon(info.getValue())} text-blue-600 dark:text-blue-400 text-[10px]`}></i>
            </div>
            <span className="font-medium text-gray-900 dark:text-white text-xs">{info.getValue()}</span>
          </div>
        </TableCell>
      ),
    }),
    columnHelper.accessor("leave_status", {
      id: "leave_status",
      header: () => <TableHeader>STATUS</TableHeader>,
      cell: (info) => <TableCell>{getStatusBadge(info.getValue())}</TableCell>,
    }),
    columnHelper.accessor("start_date", {
      id: "start_date",
      header: () => <TableHeader>START</TableHeader>,
      cell: (info) => (
        <TableCell>
          <div className="flex items-center gap-1.5">
            <i className="fas fa-calendar-alt text-gray-400 text-[10px]"></i>
            <span className="text-gray-900 dark:text-white text-xs">{formatDate(info.getValue())}</span>
          </div>
        </TableCell>
      ),
    }),
    columnHelper.accessor("end_date", {
      id: "end_date",
      header: () => <TableHeader>END</TableHeader>,
      cell: (info) => (
        <TableCell>
          <div className="flex items-center gap-1.5">
            <i className="fas fa-calendar-alt text-gray-400 text-[10px]"></i>
            <span className="text-gray-900 dark:text-white text-xs">{formatDate(info.getValue())}</span>
          </div>
        </TableCell>
      ),
    }),
    columnHelper.display({
      id: "total_days",
      header: () => <TableHeader>TOTAL DAYS</TableHeader>,
      cell: (info) => {
        const row = info.row.original;
        const days = calculateDays(row.start_date, row.end_date);
        return (
          <TableCell>
            <div className="flex items-center gap-1.5">
              <i className="fas fa-calendar-week text-gray-400 dark:text-gray-400 text-[10px]"></i>
              <span className="font-semibold text-gray-900 dark:text-gray-400 text-xs">
                {days} {days === 1 ? "day" : "days"}
              </span>
            </div>
          </TableCell>
        );
      },
    }),
    columnHelper.accessor("id", {
      id: "view",
      header: () => <TableHeader>VIEW</TableHeader>,
      cell: (info) => (
        <TableCell className="!min-w-0 !pr-0 !w-max">
          <Link to={`/leaves/${info.getValue()}`}>
            <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
              <MdVisibility className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </div>
          </Link>
        </TableCell>
      ),
    }),
  ];

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "declined", label: "Declined" },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end justify-between">
        <div className="w-full sm:w-auto sm:max-w-xs">
          <SelectField
            id="status_filter"
            label="Filter by Status"
            options={statusOptions}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as LeaveStatusFilter)}
          />
        </div>
        <div className="w-full sm:max-w-lg">
          <InputField
            id="search"
            label="Search"
            type="text"
            placeholder="Search by employee name or leave type..."
            extra="w-full"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="!bg-white dark:!bg-gray-800"
          />
        </div>
      </div>

      {searchTerm && (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing {filteredData.length} of {getCurrentData().length} {statusFilter} leave{getCurrentData().length !== 1 ? 's' : ''}
        </div>
      )}
      
      <Table<LeaveDetailsType>
        tableData={filteredData}
        columns={columns}
        tableHeading={getTableHeading()}
      />
    </div>
  );
};

export default UnifiedLeavesTable;